import { apiClient, fetcher } from './apiClient';
import type { Asset } from './Asset';
import type { Device } from './Device';

export type PropEntry = {
    key: string;
    value: string;
};

export type ShellComponent = {
    id: string;
    orgId: string;
    name: string;
    /** What devices actually load. Set from the asset when one is selected. */
    url: string;
    /** Non-null when `url` came from an uploaded asset rather than being typed in. */
    assetId: string | null;
    asset?: Asset | null;
    props: PropEntry[] | null;
    scenesCount?: number;
    createdAt?: string;
    updatedAt?: string;
};

/**
 * A component is defined by either an uploaded asset or a typed URL. Sending
 * `assetId` makes the server derive `url` from it; sending `url` with
 * `assetId: null` clears any previous asset link.
 */
export type ComponentSource = { assetId: string; url?: never } | { url: string; assetId?: null };

export type SceneTemplateId = 'full' | '20-80' | '20-60-20';

export type ScenePanel = {
    id: string;
    sceneId: string;
    componentId: string | null;
    index: number;
    heightPercent: number;
    propOverrides: PropEntry[] | null;
    component?: ShellComponent;
};

export type Scene = {
    id: string;
    orgId: string;
    name: string;
    templateId: string;
    panels?: ScenePanel[];
    playlistsCount?: number;
};

export type PlaylistSceneEntry = {
    id: string;
    playlistId: string;
    sceneId: string;
    position: number;
    durationSeconds: number;
    scene?: Scene;
};

export type Playlist = {
    id: string;
    orgId: string;
    name: string;
    playlistScenes?: PlaylistSceneEntry[];
    devicesCount?: number;
};

export type DeviceV2Config = {
    deviceId: string;
    playlistId: string | null;
    playlist: Playlist | null;
};

export const SCENE_TEMPLATES: Array<{
    id: SceneTemplateId;
    label: string;
    panels: number[];
}> = [
    { id: 'full', label: 'Full screen', panels: [100] },
    { id: '20-80', label: '20 / 80', panels: [20, 80] },
    { id: '20-60-20', label: '20 / 60 / 20', panels: [20, 60, 20] },
];

/**
 * The portrait screen a scene is authored against, used only to turn each
 * template's height percentage into the pixel size an artist should export at.
 *
 * The shell itself lays panels out in percentages (see IframePool's
 * panelBoxStyle), so nothing here affects rendering — it's guidance for whoever
 * is making the artwork.
 */
export const SCREEN_WIDTH_PX = 1020;
export const SCREEN_HEIGHT_PX = 1800;

/** Pixel height of a panel occupying `heightPercent` of the screen. */
export const panelPixelHeight = (heightPercent: number): number =>
    Math.round((heightPercent / 100) * SCREEN_HEIGHT_PX);

/** e.g. `1020 × 360` for a 20% panel. */
export const panelSizeLabel = (heightPercent: number): string =>
    `${SCREEN_WIDTH_PX} × ${panelPixelHeight(heightPercent)}`;

/**
 * Position names for a template's panels, top to bottom. Panels are always
 * full-width horizontal bands, so position is the only thing to name.
 */
export const panelPositionLabels = (panelCount: number): string[] => {
    if (panelCount === 1) return ['Full screen'];
    if (panelCount === 2) return ['Top', 'Bottom'];
    return ['Top', 'Middle', 'Bottom'];
};

const jsonHeaders = (csrfToken: string | null | undefined) => ({
    'Content-Type': 'application/json',
    'X-XSRF-TOKEN': csrfToken || '',
});

export const listComponents = () => fetcher<ShellComponent[]>('api/components');
export const getComponent = (id: string) => fetcher<ShellComponent>(`api/components/${id}`);

export const createComponent = async (
    payload: { name: string; props: PropEntry[] } & ComponentSource,
    csrfToken: string | null | undefined
) => {
    const res = await apiClient.post('api/components', JSON.stringify(payload), {
        headers: jsonHeaders(csrfToken),
    });
    return JSON.parse(res.data) as ShellComponent;
};

export const updateComponent = async (
    id: string,
    payload: { name: string; props: PropEntry[] } & ComponentSource,
    csrfToken: string | null | undefined
) => {
    const res = await apiClient.put(`api/components/${id}`, JSON.stringify(payload), {
        headers: jsonHeaders(csrfToken),
    });
    return JSON.parse(res.data) as ShellComponent;
};

export const deleteComponent = async (id: string, csrfToken: string | null | undefined) => {
    await apiClient.delete(`api/components/${id}`, {
        headers: jsonHeaders(csrfToken),
    });
};

/**
 * Ask devices to remount the iframe(s) rendering a component without a config change.
 * Pass a deviceId to scope to one screen; omit it to refresh every device showing it.
 */
export const reloadComponent = async (
    id: string,
    deviceId: string | null | undefined,
    csrfToken: string | null | undefined
) => {
    await apiClient.post(
        `api/components/${id}/reload`,
        JSON.stringify(deviceId ? { deviceId } : {}),
        { headers: jsonHeaders(csrfToken) }
    );
};

/**
 * Ask a single device to remount the iframe(s) for a component (componentId), or every
 * iframe on the device (componentId omitted), without a config change.
 */
export const reloadDevice = async (
    deviceId: string,
    componentId: string | null | undefined,
    csrfToken: string | null | undefined
) => {
    await apiClient.post(
        `api/device/${deviceId}/reload`,
        JSON.stringify(componentId ? { componentId } : {}),
        { headers: jsonHeaders(csrfToken) }
    );
};

export const listScenes = () => fetcher<Scene[]>('api/scenes');
export const getScene = (id: string) => fetcher<Scene>(`api/scenes/${id}`);

export const createScene = async (
    payload: {
        name: string;
        templateId: SceneTemplateId;
        panels: Array<{ instanceId?: string; componentId: string; propOverrides: PropEntry[] }>;
    },
    csrfToken: string | null | undefined
) => {
    const res = await apiClient.post('api/scenes', JSON.stringify(payload), {
        headers: jsonHeaders(csrfToken),
    });
    return JSON.parse(res.data) as Scene;
};

export const updateScene = async (
    id: string,
    payload: {
        name: string;
        templateId: SceneTemplateId;
        panels: Array<{ instanceId?: string; componentId: string; propOverrides: PropEntry[] }>;
    },
    csrfToken: string | null | undefined
) => {
    const res = await apiClient.put(`api/scenes/${id}`, JSON.stringify(payload), {
        headers: jsonHeaders(csrfToken),
    });
    return JSON.parse(res.data) as Scene;
};

export const deleteScene = async (id: string, csrfToken: string | null | undefined) => {
    await apiClient.delete(`api/scenes/${id}`, {
        headers: jsonHeaders(csrfToken),
    });
};

export const listPlaylists = () => fetcher<Playlist[]>('api/playlists');
export const getPlaylist = (id: string) => fetcher<Playlist>(`api/playlists/${id}`);

/** Devices currently bound to a playlist, ordered by name — powers the device chips. */
export const listPlaylistDevices = (id: string) =>
    fetcher<Device[]>(`api/playlists/${id}/devices`);

export const createPlaylist = async (
    payload: {
        name: string;
        scenes: Array<{ sceneId: string; durationSeconds: number }>;
    },
    csrfToken: string | null | undefined
) => {
    const res = await apiClient.post('api/playlists', JSON.stringify(payload), {
        headers: jsonHeaders(csrfToken),
    });
    return JSON.parse(res.data) as Playlist;
};

export const updatePlaylist = async (
    id: string,
    payload: {
        name: string;
        scenes: Array<{ sceneId: string; durationSeconds: number }>;
    },
    csrfToken: string | null | undefined
) => {
    const res = await apiClient.put(`api/playlists/${id}`, JSON.stringify(payload), {
        headers: jsonHeaders(csrfToken),
    });
    return JSON.parse(res.data) as Playlist;
};

export const deletePlaylist = async (id: string, csrfToken: string | null | undefined) => {
    await apiClient.delete(`api/playlists/${id}`, {
        headers: jsonHeaders(csrfToken),
    });
};

/**
 * The resolved runtime snapshot the device player boots from (camelCase at the source).
 * Note: the component ref here carries id/url/props only — NOT name. Join against
 * listComponents() for a human-readable label.
 */
export type ShellSnapshotComponentRef = {
    id: string;
    url: string;
    props: PropEntry[];
};

export type ShellSnapshotPanel = {
    instanceId: string;
    index: number;
    heightPercent: number;
    component: ShellSnapshotComponentRef;
};

export type ShellSnapshotScene = {
    id: string;
    name: string;
    durationSeconds: number;
    templateId: string;
    panels: ShellSnapshotPanel[];
};

export type ShellSnapshot = {
    machineId: string;
    updatedAt: number;
    device: { deviceId: string; locationName: string | null };
    playlist: { id: string; name: string; scenes: ShellSnapshotScene[] };
};

export const getDeviceShellSnapshot = (deviceId: string) =>
    fetcher<ShellSnapshot>(`api/device-shell/${deviceId}`);

export const getDeviceV2Config = (deviceId: string) =>
    fetcher<DeviceV2Config>(`api/device/${deviceId}/v2config`);

export const updateDeviceV2Config = async (
    deviceId: string,
    playlistId: string | null,
    csrfToken: string | null | undefined
) => {
    const res = await apiClient.put(
        `api/device/${deviceId}/v2config`,
        JSON.stringify({ playlistId }),
        { headers: jsonHeaders(csrfToken) }
    );
    return JSON.parse(res.data) as DeviceV2Config;
};
